import type { Dashboard, DashboardQuery } from '@pixerito/api-client';
import { monthLong, monthName, monthShort, shiftPeriod } from './format';

/** Intervalo de meses, as duas pontas incluídas ("aaaa-mm"). Um mês só tem from === to. */
export interface PeriodRange {
  from: string;
  to: string;
}

/** Mesmo limite da API (MonthRange.MaxLength). */
export const MAX_RANGE_MONTHS = 24;

/** Atalhos de período. Sempre terminam no mês mais recente com dados. */
export const QUICK_RANGES = [
  { key: 'month', label: 'Mensal', months: 1 },
  { key: 'quarter', label: 'Trimestral', months: 3 },
  { key: 'semester', label: 'Semestral', months: 6 },
  { key: 'year', label: 'Anual', months: 12 },
] as const;

export type QuickKey = (typeof QUICK_RANGES)[number]['key'];

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isPeriod(value: string | null | undefined): value is string {
  return !!value && PERIOD_RE.test(value);
}

function monthIndex(period: string) {
  const [y, m] = period.split('-').map(Number);
  return y * 12 + (m - 1);
}

/** Quantidade de meses, contando as duas pontas. */
export function rangeLength(range: PeriodRange) {
  return monthIndex(range.to) - monthIndex(range.from) + 1;
}

export function sameRange(a: PeriodRange, b: PeriodRange) {
  return a.from === b.from && a.to === b.to;
}

/** Coloca as pontas em ordem: quem clica o mês final antes do inicial continua certo. */
export function orderRange(a: string, b: string): PeriodRange {
  return a <= b ? { from: a, to: b } : { from: b, to: a };
}

/** Os últimos `months` meses, terminando em `end`. */
export function quickRange(months: number, end: string): PeriodRange {
  return { from: shiftPeriod(end, -(months - 1)), to: end };
}

/** Anda o intervalo inteiro para trás (-1) ou para a frente (+1), pelo tamanho dele. Um mês anda um mês. */
export function shiftRange(range: PeriodRange, direction: 1 | -1): PeriodRange {
  const months = rangeLength(range) * direction;
  return { from: shiftPeriod(range.from, months), to: shiftPeriod(range.to, months) };
}

/** Qual atalho o intervalo é, se for algum: mesmo tamanho e terminando no mês mais recente. */
export function activeQuick(range: PeriodRange, latest: string | undefined): QuickKey | null {
  if (!latest || range.to !== latest) return null;
  const length = rangeLength(range);
  return QUICK_RANGES.find((q) => q.months === length)?.key ?? null;
}

// ── Seleção no calendário (dois cliques) ────────────────────────

/** O intervalo que o calendário destaca: o que está sendo montado (âncora + mouse) ou o já escolhido. */
export function previewRange(chosen: PeriodRange, anchor: string | null, hover: string | null): PeriodRange {
  return anchor ? orderRange(anchor, hover ?? anchor) : chosen;
}

/** Com a âncora marcada, meses que fariam o intervalo passar do limite ficam desabilitados. */
export function monthDisabled(month: string, anchor: string | null, bounds: { earliest?: string; latest?: string }) {
  if (bounds.earliest && month < bounds.earliest) return true;
  if (bounds.latest && month > bounds.latest) return true;
  if (anchor && rangeLength(orderRange(anchor, month)) > MAX_RANGE_MONTHS) return true;
  return false;
}

// ── Textos ──────────────────────────────────────────────────────

const year = (period: string) => period.slice(0, 4);
const abbr = (period: string) => monthName(period).slice(0, 3);

/** "agosto" · "julho a setembro" · "outubro de 2025 a setembro de 2026" (para "Resultados de …"). */
export function rangeName({ from, to }: PeriodRange) {
  if (from === to) return monthName(from).toLowerCase();
  if (year(from) === year(to)) return `${monthName(from).toLowerCase()} a ${monthName(to).toLowerCase()}`;
  return `${monthLong(from)} a ${monthLong(to)}`;
}

/** "agosto de 2026" · "julho a setembro de 2026" · "outubro de 2025 a setembro de 2026". */
export function rangeLong({ from, to }: PeriodRange) {
  if (from === to) return monthLong(from);
  if (year(from) === year(to)) return `${monthName(from).toLowerCase()} a ${monthLong(to)}`;
  return `${monthLong(from)} a ${monthLong(to)}`;
}

/** "ago/26" · "jul–set/26" · "out/25 – set/26" (rótulos curtos de comparação). */
export function rangeShort({ from, to }: PeriodRange) {
  if (from === to) return monthShort(from);
  if (year(from) === year(to)) return `${monthShort(from).split('/')[0]}–${monthShort(to)}`;
  return `${monthShort(from)} – ${monthShort(to)}`;
}

/** Texto do botão: "Agosto 2026" · "Jul – Set 2026" · "Out 2025 – Set 2026". */
export function rangeLabel({ from, to }: PeriodRange) {
  if (from === to) return `${monthName(from)} ${year(from)}`;
  if (year(from) === year(to)) return `${abbr(from)} – ${abbr(to)} ${year(to)}`;
  return `${abbr(from)} ${year(from)} – ${abbr(to)} ${year(to)}`;
}

// ── Dados e endereço (URL) ──────────────────────────────────────

/** O período que o dashboard recebido cobre (a API sempre devolve `range`; `period` cobre servidor antigo). */
export function dashboardRange(data: Pick<Dashboard, 'period' | 'range'>): PeriodRange | null {
  if (data.range) return { from: data.range.from, to: data.range.to };
  return data.period ? { from: data.period, to: data.period } : null;
}

/** O período anterior, o da comparação. */
export function previousRange(data: Pick<Dashboard, 'previousPeriod' | 'range'>): PeriodRange | null {
  if (data.range) return { from: data.range.previousFrom, to: data.range.previousTo };
  return data.previousPeriod ? { from: data.previousPeriod, to: data.previousPeriod } : null;
}

/**
 * Lê o período do endereço: `?mes=2026-08` (um mês) ou `?de=2026-07&ate=2026-09` (intervalo).
 * Valor inválido ou intervalo grande demais é ignorado e o painel abre no mês mais recente.
 */
export function queryFromParams(params: URLSearchParams): DashboardQuery | null {
  const from = params.get('de');
  const to = params.get('ate');
  if (isPeriod(from) && isPeriod(to)) {
    const range = orderRange(from, to);
    if (rangeLength(range) <= MAX_RANGE_MONTHS) return { from: range.from, to: range.to };
  }
  const month = params.get('mes');
  return isPeriod(month) ? { period: month } : null;
}

/** O inverso de `queryFromParams`: um mês vira `mes`, vários viram `de` e `ate`. */
export function paramsFromRange(range: PeriodRange): Record<string, string> {
  return range.from === range.to ? { mes: range.from } : { de: range.from, ate: range.to };
}
